<template>
  <div>
      <CRow>
        <!-- Table with institute results -->
        <BootstrapVueTable style="width: 450px; height: 450px" class="ml-3"
          :title="'Résultats au ' + reference_date"
          :items="table_data"
          :fields="table_fields"
          :search=false
        />

        <!-- Chart with cumulative results per month -->
        <CCol >
          <CCard style="height: 450px" class="ml-2">
            <CCardBody>
              <CChartLine class="h-100"
                :datasets="cumulative_chart_data"
                :options="{ ...common_chart_options, title: {display: true, text: `Evolution durant l'année (cumulé)`, fontSize: 14}}"
                :labels="months"
              />
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <!-- Chart with the monthly changes -->
      <CRow>
        <CCol>
          <CCard  class="col-12" style="height: 600px">
            <CCardBody>
              <CChartBar class="h-100 pb-3"
                :datasets="monthly_chart_data"
                :options="{ ...common_chart_options, title: {display: true, text: `Evolution durant l'année (mensuel)`, fontSize: 14}}"
                :labels="months"
              />
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
  </div>
</template>

<script>
import axios from 'axios'
import { add, subtract, multiply } from 'mathjs'
import errorRoutingMixin from '@/mixins/errorRoutingMixin'
import data_formats from '@/utils/data_formats'
import BootstrapVueTable from '@/components/BootstrapVueTable'
import { CChartLine, CChartBar } from '@coreui/vue-chartjs'

export default {
  name: 'Institute_results',
  mixins: [errorRoutingMixin],
  props: ['reference_entity', 'reference_year', 'reference_month'],
  components: {BootstrapVueTable, CChartLine, CChartBar},
  data () {
    return {
      raw_data: [],
      months: [1,2,3,4,5,6,7,8,9,10,11,12],
      table_data: [],
      table_fields: [
        {key: 'parameter', label: ''}, 
        {key: 'accounting', label: 'Comptabilité', class: 'text-right font-weight-bold', formatter: data_formats.number_format_frch_rounded},
        {key: 'agp', label: 'AGP', class: 'text-right text-muted', formatter: data_formats.number_format_frch_rounded}
      ],

      //palette: ['RoyalBlue', 'CornflowerBlue', 'IndianRed', 'Coral', 'Tomato', 'Gold', 'Crimson'],
      palette: ['RoyalBlue', 'OrangeRed', 'LightSalmon', 'Firebrick', 'DarkSalmon', 'Salmon', 'Gold'],
      cumulative_chart_data: [],
      monthly_chart_data: [],
      common_chart_options: {
        maintainAspectRatio: false,
        legend: { display: true, position: "bottom", labels: {usePointStyle: true, fontSize: 12, boxWidth: 5} },
        scales: {
          xAxes: [{ gridLines: { drawOnChartArea: true }, scaleLabel: {display: true, labelString: "Mois"} }],
          yAxes: [{ ticks: { beginAtZero: true }, gridLines: { display: true }, scaleLabel: {display: true, labelString: "Montant (CHF)"}}]
        }
      }
    }
  },
  watch: {
    all_props () {
      this.load_server_data()
    }
  },
  created () {
    this.load_server_data()
  },
  computed: {
    all_props () {
      return [
        this.reference_entity,
        this.reference_year,
        this.reference_month  
      ]
    },
    reference_date () {
      return new Date(this.reference_year, this.reference_month, 0).toLocaleDateString('fr-CH') 
    },
  },
  methods: {

    load_server_data () {
      if (! this.reference_entity) return  // If user is not yet authenticated. do nothing
      let url = 'webapi/institute-results?entity=' + this.reference_entity + '&year=' + this.reference_year + '&month=' + this.reference_month 
      axios.get(url, {headers: {'Authorization': 'Bearer ' + this.$cookies.get('jwt_access')}})
        .then(response => this.raw_data = response.data)
        .then(_ => this.fill_data())
        .catch(error => {
          console.log(error)
          this.$router.push(this.routeOnError(error.response.status))
        })
    },

    fill_data() {
      // Enrich the data with additional computed columns
      let cumulative = {}
      for (var key of Object.keys(this.raw_data)) {
        cumulative[key] = this.raw_data[key].map(Math.round)
      }
      cumulative.operationalResultAgp = subtract(cumulative.AccountingTurnover, add(cumulative.AgpCostHes, cumulative.AgpCostFdt, cumulative.AgpCostBsm))
      cumulative.operationalResultAccounting = subtract(cumulative.AccountingTurnover, add(cumulative.CONTRIBUTIONHES, cumulative.SalairesFdt, cumulative.AccountingCostBsm, 
                                                                                           multiply(cumulative.RECEIVABLESPROVISIONS, -1)))
      cumulative.instituteResultAgp = subtract(cumulative.operationalResultAgp, cumulative.AccountingManagementFees) 
      cumulative.instituteResultAccounting = subtract(cumulative.operationalResultAccounting, cumulative.AccountingManagementFees) 

      let monthly = {}
      for (var key of Object.keys(cumulative)) {
        monthly[key] = subtract(cumulative[key], [0].concat(cumulative[key].slice(0,-1))) // Compute the difference with previous month
      }

      // Create the different table and chart dataset
      this.table_data = this.fill_table_data(cumulative, this.reference_month)
      this.cumulative_chart_data = this.format_cumulative_chart(this.fill_chart_data(cumulative))
      this.monthly_chart_data = this.format_monthly_chart(this.fill_chart_data(monthly))
    },

    fill_table_data(d, month) {
      let i = month - 1
      return [
        {parameter: "Chiffre d'affaires (+ prov. risques)", agp: "---", accounting: d.AccountingTurnover[i] + d.AccountingLossProvision[i], _rowVariant: 'warning'},
        {parameter: "Coût personnel HES", agp: - d.AgpCostHes[i], accounting: - d.CONTRIBUTIONHES[i]},
        {parameter: "Coût personnel Fdt", agp: - d.AgpCostFdt[i], accounting: - d.SalairesFdt[i]},
        {parameter: "Coût BSM", agp: - d.AgpCostBsm[i], accounting: - d.AccountingCostBsm[i]},
        {parameter: "Création/diss. risques créances", agp: "---" , accounting: d.RECEIVABLESPROVISIONS[i]},
        //{parameter: "Résultat opérationnel", agp: d.operationalResultAgp[i], accounting: d.operationalResultAccounting[i], _rowVariant: 'warning'},
        {parameter: "Provisions risques projets", agp: "---", accounting: - d.AccountingLossProvision[i]},
        {parameter: "Frais de gestion", agp: "---", accounting: - d.AccountingManagementFees[i]},
        {parameter: "Résultat de l'institut", agp: d.instituteResultAgp[i], accounting: d.instituteResultAccounting[i], _rowVariant: 'info'},
        {parameter: "Socle disponible", agp: "---" , accounting: d.BASEFUNDING[i], _rowVariant: 'light'},
        {parameter: "Bénéfices mandats prévus", agp: "---" , accounting: d.AnticipatedProfit[i], _rowVariant: 'light'}
      ]
    },

    fill_chart_data(d) {
      return [
        { label: 'Chiffre d\'affaires', data: d.AccountingTurnover},
        { label: 'Salaires Fdt', data: d.SalairesFdt},
        { label: 'Contribution HES', data: d.CONTRIBUTIONHES},
        { label: 'Coût BSM', data: d.AccountingCostBsm},
        //{ label: 'Frais de gestion', data: d.AccountingManagementFees},
        { label: 'Résultat compt.', data: d.instituteResultAccounting}
      ]
    },

    format_cumulative_chart(datasets) {
      let dash = [8,5]
      let format = [
        {borderColor: this.palette[0], hoverBorderColor: this.palette[0], borderWidth: 2, backgroundColor: 'transparent', borderDash: []},
        {borderColor: this.palette[1], hoverBorderColor: this.palette[1], borderWidth: 2, backgroundColor: 'transparent', borderDash: []},
        {borderColor: this.palette[2], hoverBorderColor: this.palette[2], borderWidth: 2, backgroundColor: 'transparent', borderDash: []},
        {borderColor: this.palette[3], hoverBorderColor: this.palette[3], borderWidth: 2, backgroundColor: 'transparent', borderDash: []},
        //{borderColor: this.palette[4], hoverBorderColor: this.palette[4], borderWidth: 2, backgroundColor: 'transparent', borderDash: []},
        //{borderColor: this.palette[5], hoverBorderColor: this.palette[5], borderWidth: 2, backgroundColor: 'transparent', borderDash: []},
        {borderColor: this.palette[6], hoverBorderColor: this.palette[6], borderWidth: 2, backgroundColor: 'transparent', borderDash: []}
      ]
      return datasets.map((v, i) => ({...v, ...format[i]}))
    },

    format_monthly_chart(datasets) {
      let bars = {barPercentage: 1.0, categoryPercentage: 0.7}
      let format = [
        {borderColor: this.palette[0], hoverBorderColor: this.palette[0], borderWidth: 1, backgroundColor: this.palette[0], ...bars},
        {borderColor: this.palette[1], hoverBorderColor: this.palette[1], borderWidth: 1, backgroundColor: this.palette[1], ...bars},
        {borderColor: this.palette[2], hoverBorderColor: this.palette[2], borderWidth: 1, backgroundColor: this.palette[2], ...bars},
        {borderColor: this.palette[3], hoverBorderColor: this.palette[3], borderWidth: 1, backgroundColor: this.palette[3], ...bars},
        //{borderColor: this.palette[4], hoverBorderColor: this.palette[4], borderWidth: 1, backgroundColor: this.palette[4], ...bars},
        //{borderColor: this.palette[5], hoverBorderColor: this.palette[5], borderWidth: 1, backgroundColor: this.palette[5], ...bars},
        {borderColor: this.palette[6], hoverBorderColor: this.palette[6], borderWidth: 1, backgroundColor: this.palette[6], ...bars}
      ]
      return datasets.map((v, i) => ({...v, ...format[i]}))
    },
  }
}
</script>
